import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import propertyPaymentsService from "../services/propertyPaymentsService";
import { downloadCSV } from "../utils/exportUtils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import "../assets/propertyPayments.css";

// Column definitions for the payment list table
const PAYMENT_COLUMNS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'fee', label: 'Fee' },
  { key: 'room', label: 'Room' },
  { key: 'due_date', label: 'Due' },
  { key: 'moved_in_date', label: 'Moved In' },
  { key: 'outstanding_amount', label: 'O/A' },
  { key: 'payment_status', label: 'Payment Status' },
  { key: 'rent_status', label: 'Status' },
];

// Format number with commas and 2 decimal places (matching Laravel's number_format)
const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const PropertyPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if current user is SALES role
  const isSales = () => {
    if (!user) return false;
    if (user.role === 'SALES') return true;
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => {
        if (typeof role === 'string') return role === 'SALES';
        if (role && typeof role === 'object' && role.name) return role.name === 'SALES';
        return false;
      });
    }
    return false;
  };

  const userIsSales = isSales();

  const [propertyData, setPropertyData] = useState({
    property: null,
    payments: [],
    statistics: {
      monthlyAmountOccupiedRooms: 0,
      totalAmountPaidByGuardians: 0,
    },
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("first_name");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);

  // Column visibility state - all visible by default
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const defaults = {};
    PAYMENT_COLUMNS.forEach(col => { defaults[col.key] = true; });
    return defaults;
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const columnVisibilityRef = useRef(null);

  // Helper function to check if payment is overdue (matching Laravel logic)
  const isPaymentOverdue = (payment) => {
    if (!payment.due_date) return false;
    const now = new Date();
    const dueDate = new Date(payment.due_date.split("/").reverse().join("-")); // Convert DD/MM/YYYY to YYYY-MM-DD
    now.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < now;
  };

  // Helper function to check if payment is not paid (matching Laravel logic)
  const isPaymentNotPaid = (payment) => {
    // Laravel checks for status 0 (not_paid)
    return (
      payment.payment_status === "Overdue" ||
      payment.payment_status === "Not paid"
    );
  };

  // Helper function to check if payment is paid (matching Laravel logic)
  const isPaymentPaid = (payment) => {
    return payment.payment_status === "Paid";
  };

  // Filter and sort payments
  const getFilteredAndSortedPayments = () => {
    let filtered = propertyData.payments;

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((payment) => {
        switch (filterStatus) {
          case "paid":
            return isPaymentPaid(payment);
          case "not_paid":
            return isPaymentNotPaid(payment);
          case "overdue":
            return isPaymentOverdue(payment);
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (payment) =>
          (payment.first_name || "").toLowerCase().includes(term) ||
          (payment.last_name || "").toLowerCase().includes(term) ||
          (payment.room || "").toLowerCase().includes(term)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date sorting
      if (sortBy === "due_date" || sortBy === "moved_in_date") {
        aValue = aValue
          ? new Date(aValue.split("/").reverse().join("-"))
          : new Date(0);
        bValue = bValue
          ? new Date(bValue.split("/").reverse().join("-"))
          : new Date(0);
      }

      // Handle numeric sorting
      if (sortBy === "fee") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredPayments = getFilteredAndSortedPayments();

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / entriesPerPage));
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Sort handler for clickable column headers
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnVisibilityRef.current && !columnVisibilityRef.current.contains(event.target)) {
        setShowColumnVisibility(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleColumnVisibility = (key) => {
    setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExportCSV = () => {
    const visibleCols = PAYMENT_COLUMNS.filter(col => columnVisibility[col.key]);
    if (visibleCols.length === 0) {
      toast.warn("No columns selected for export");
      return;
    }
    // Build export data with readable headers
    const exportData = filteredPayments.map(payment => {
      const row = {};
      visibleCols.forEach(col => {
        row[col.label] = payment[col.key] ?? "";
      });
      return row;
    });
    const propertyName = (propertyData.property?.name || "property").replace(/[^a-zA-Z0-9]/g, "_");
    const filterSuffix = filterStatus !== "all" ? `_${filterStatus}` : "";
    downloadCSV(exportData, `${propertyName}_payments${filterSuffix}.csv`);
  };

  useEffect(() => {
    loadPropertyPayments();
  }, [id]);

  const loadPropertyPayments = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("🔍 [PropertyPayments] Calling API for property ID:", id);

      // Call the payments service
      const response = await propertyPaymentsService.getPropertyPayments(id);

      console.log("🔍 [PropertyPayments] API Response:", response);
      console.log("🔍 [PropertyPayments] Response data:", response.data);
      console.log(
        "🔍 [PropertyPayments] Response success:",
        response.data?.success
      );

      // Check if the response has the expected structure
      if (response.data && response.data.success) {
        console.log(
          "🔍 [PropertyPayments] Setting property data:",
          response.data.data
        );
        setPropertyData(response.data.data);
        setError(""); // Clear any previous errors
      } else {
        console.error(
          "❌ [PropertyPayments] API response structure invalid:",
          response
        );
        setError(
          "Failed to load property payments - Invalid response structure"
        );
      }
    } catch (error) {
      console.error(
        "❌ [PropertyPayments] Error loading property payments:",
        error
      );
      setError("Failed to load property payments. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (action, payment) => {
    switch (action) {
      case "edit":
        navigate(`/rents/${payment.rent_id}/payments/${payment.id}/edit`);
        break;
      case "mark-as-paid":
        if (
          window.confirm(`Mark payment as paid for ${payment.guardian_name}?`)
        ) {
          handleMarkAsPaid(payment.id, payment);
        }
        break;
      case "send-reminder":
        if (
          window.confirm(`Send payment reminder to ${payment.guardian_name}?`)
        ) {
          handleSendReminder(payment.id, payment);
        }
        break;
      case "delete":
        if (
          window.confirm(
            `Are you sure you want to delete the payment for ${payment.guardian_name}? This action cannot be undone.`
          )
        ) {
          handleDeletePayment(payment.id, payment);
        }
        break;
      default:
        console.warn(`Unknown action: ${action}`);
        break;
    }
  };

  const handleDeletePayment = async (paymentId, payment) => {
    try {
      console.log(`Deleting payment ${paymentId} for ${payment.guardian_name}`);
      // Mock delete functionality - in real implementation, call API
      // await paymentsService.deletePayment(payment.rent_id, paymentId);
      toast.success(
        `Payment for ${payment.guardian_name} deleted successfully`
      );
      loadPropertyPayments();
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    }
  };

  const handleSendReminder = async (paymentId, payment) => {
    const confirmMessage = `Are you sure you want to send a payment reminder to ${payment.guardian_name}?`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log(
          `Sending reminder for payment ${paymentId} to ${payment.guardian_name} (${payment.mobile})`
        );
        const response = await propertyPaymentsService.sendPaymentReminder(
          paymentId
        );

        if (response.success) {
          toast.success("You sent the payment reminder!");
        } else {
          toast.error(response.message || "Failed to send payment reminder");
        }
      } catch (error) {
        console.error("Error sending reminder:", error);
        toast.error("Failed to send payment reminder");
      }
    }
  };
  const handleMarkAsPaid = async (paymentId, payment) => {
    const confirmMessage = `Are you sure you want to mark payment of £${payment.fee} as paid for ${payment.guardian_name}?`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log(
          `Marking payment ${paymentId} as paid for ${payment.guardian_name}`
        );
        const response = await propertyPaymentsService.markPaymentAsPaid(
          payment.rent_id,
          paymentId
        );

        if (response && response.success) {
          toast.success(`Payment marked as paid for ${payment.guardian_name}`);
          await loadPropertyPayments(); // Reload data to reflect changes
        } else {
          toast.error(response?.message || "Failed to mark payment as paid");
        }
      } catch (error) {
        console.error("Error marking payment as paid:", error);
        toast.error("Failed to mark payment as paid");
      }
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading property payments...</div>
      </div>
    );
  }

  if (error) {
    console.log("🔍 [PropertyPayments] Rendering error state:", error);
    console.log("🔍 [PropertyPayments] Current propertyData:", propertyData);
    return (
      <div className="container">
        <div className="error-message">{error}</div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setError("");
            loadPropertyPayments();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumbs - matching Laravel */}
      <ol className="breadcrumb">
        <li>
          <Link to="/dashboard">Dashboard</Link>
        </li>
        <li>
          <Link to="/properties">Properties</Link>
        </li>
        <li>
          <Link to={`/properties/${id}`}>
            {propertyData.property?.name || "Property"}
          </Link>
        </li>
        <li className="active">
          <span className="breadcrumb-payment-list">Payment List</span>
        </li>
      </ol>

      {/* Property Overview - matching Laravel _property_overview partial */}
      <div className="pp-section-header">
        <h3>Property overview</h3>
      </div>
      <div className="pp-section-body">
        {propertyData.property ? (
          <dl className="list green-list green-list-with-width">
            <dt>Property ID:</dt>
            <dd>{propertyData.property.property_id}</dd>

            <dt>Property Name:</dt>
            <dd>{propertyData.property.name}</dd>

            <dt>Property Status:</dt>
            <dd>{propertyData.property.status_text}</dd>

            <dt>Property address:</dt>
            <dd>{propertyData.property.address_1}</dd>

            <dt>Postcode:</dt>
            <dd>{propertyData.property.postcode}</dd>

            {propertyData.property.date_live && propertyData.property.date_taken_on && (
              <>
                <dt>Lease dates:</dt>
                <dd>
                  {propertyData.property.date_taken_on}
                  {propertyData.property.date_taken_back && (
                    <> to {propertyData.property.date_taken_back}</>
                  )}
                </dd>
              </>
            )}

            {propertyData.property.date_notice && (
              <>
                <dt>Notice date:</dt>
                <dd>{propertyData.property.date_notice}</dd>
              </>
            )}
          </dl>
        ) : (
          <p>No property data available.</p>
        )}
      </div>

      {/* Statistics - matching Laravel payments/properties/statistics partial */}
      <div className="pp-section-header">
        <h3>Statistics</h3>
      </div>
      <div className="pp-section-body">
        {propertyData.statistics ? (
          <div className="statistics-row">
            <div className="statistics-col">
              <div className="saving-box red">
                <p className="cash">
                  £{formatCurrency(propertyData.statistics.monthlyAmountOccupiedRooms || propertyData.statistics.totalPriceOccupiedRooms || 0)}
                </p>
                <p className="saving-information">
                  MONTHLY AMOUNT OF OCCUPIED ROOMS
                </p>
              </div>
            </div>
            <div className="statistics-col">
              <div className="saving-box mint">
                <p className="cash">
                  £{formatCurrency(propertyData.statistics.totalAmountPaidByGuardians || 0)}
                </p>
                <p className="saving-information">
                  TOTAL AMOUNT PAID BY GUARDIANS
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p>No statistics available.</p>
        )}
      </div>

      {/* Payment List - matching Laravel payments/properties/list partial */}
      <div className="pp-section-header">
        <h3 className="set-payment-header-h3">Payment List</h3>
      </div>
      <div className="pp-section-body">
        <div className="mb-20 ml-10">
          System displays all guardians of property who are active or not paid.
        </div>

            {propertyData.payments.length > 0 ? (
              <div className="dataTable_wrapper">
                {/* DataTable toolbar */}
                <div className="dt-toolbar">
                  <div className="dt-toolbar-left">
                    <label>
                      Show{" "}
                      <select
                        className="dt-length-select"
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={75}>75</option>
                        <option value={100}>100</option>
                      </select>{" "}
                      entries
                    </label>
                  </div>
                  <div className="dt-toolbar-right">
                    <label>
                      Search:{" "}
                      <input
                        type="text"
                        className="dt-search-input"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* Filter / Export / Column Visibility toolbar */}
                <div className="dt-toolbar dt-toolbar-actions">
                  <div className="dt-toolbar-left">
                    <label>
                      Payment Status:{" "}
                      <select
                        className="dt-length-select"
                        value={filterStatus}
                        onChange={(e) => {
                          setFilterStatus(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="all">All</option>
                        <option value="paid">Paid</option>
                        <option value="not_paid">Not Paid</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </label>
                  </div>
                  <div className="dt-toolbar-right" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div ref={columnVisibilityRef} style={{ position: "relative", display: "inline-block" }}>
                      <button
                        className="btn btn-sm btn-default"
                        onClick={() => setShowColumnVisibility(!showColumnVisibility)}
                      >
                        Column visibility
                      </button>
                      {showColumnVisibility && (
                        <div className="pp-column-visibility-dropdown">
                          <h4 className="pp-cv-title">Toggle Columns:</h4>
                          <div className="pp-cv-list">
                            {PAYMENT_COLUMNS.map(col => (
                              <label key={col.key} className="pp-cv-label">
                                <input
                                  type="checkbox"
                                  checked={columnVisibility[col.key]}
                                  onChange={() => toggleColumnVisibility(col.key)}
                                />
                                <span>{col.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={handleExportCSV}
                    >
                      Export as CSV
                    </button>
                  </div>
                </div>

                <table className="table table-hover dt-responsive payment-table">
                  <thead>
                    <tr>
                      {columnVisibility.first_name && (
                        <th
                          className={`sortable ${sortBy === "first_name" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("first_name")}
                        >
                          First Name
                        </th>
                      )}
                      {columnVisibility.last_name && (
                        <th
                          className={`sortable ${sortBy === "last_name" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("last_name")}
                        >
                          Last Name
                        </th>
                      )}
                      {columnVisibility.fee && (
                        <th
                          className={`sortable ${sortBy === "fee" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("fee")}
                        >
                          Fee
                        </th>
                      )}
                      {columnVisibility.room && (
                        <th
                          className={`sortable ${sortBy === "room" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("room")}
                        >
                          Room
                        </th>
                      )}
                      {columnVisibility.due_date && (
                        <th
                          className={`sortable ${sortBy === "due_date" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("due_date")}
                        >
                          Due
                        </th>
                      )}
                      {columnVisibility.moved_in_date && (
                        <th
                          className={`sortable ${sortBy === "moved_in_date" ? (sortOrder === "asc" ? "sorting_asc" : "sorting_desc") : ""}`}
                          onClick={() => handleSort("moved_in_date")}
                        >
                          Moved In
                        </th>
                      )}
                      {columnVisibility.outstanding_amount && (
                        <th title="Outstanding Amount">O/A</th>
                      )}
                      {columnVisibility.payment_status && (
                        <th>Payment status</th>
                      )}
                      {columnVisibility.rent_status && (
                        <th>Status</th>
                      )}
                      <th className="action-head">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPayments.map((payment) => {
                      const statusLabel = payment.status_class || "default";
                      return (
                        <tr key={payment.id}>
                          {columnVisibility.first_name && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.first_name}</span>
                            </td>
                          )}
                          {columnVisibility.last_name && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.last_name}</span>
                            </td>
                          )}
                          {columnVisibility.fee && (
                            <td>
                              <span className={`label label-${statusLabel}`}>&pound;{payment.fee}</span>
                            </td>
                          )}
                          {columnVisibility.room && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.room}</span>
                            </td>
                          )}
                          {columnVisibility.due_date && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.due_date}</span>
                            </td>
                          )}
                          {columnVisibility.moved_in_date && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.moved_in_date}</span>
                            </td>
                          )}
                          {columnVisibility.outstanding_amount && (
                            <td>
                              <span className={`label label-${statusLabel}`}>&pound;{payment.outstanding_amount}</span>
                            </td>
                          )}
                          {columnVisibility.payment_status && (
                            <td>
                              <span className={`label label-${statusLabel}`}>{payment.payment_status}</span>
                            </td>
                          )}
                          {columnVisibility.rent_status && (
                            <td>{payment.rent_status}</td>
                          )}
                          <td className="action">
                            <div className="action-buttons">
                              <a
                                href={`/rents/${payment.rent_id}/payments`}
                                className="btn btn-sm btn-primary btn-action"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/rents/${payment.rent_id}/payments`);
                                }}
                              >
                                Details
                              </a>
                              {isPaymentOverdue(payment) && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() => handleSendReminder(payment.id, payment)}
                                >
                                  Send Reminder
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="dt-footer">
                  <div className="dt-footer-info">
                    Showing {propertyData.payments.length === 0 ? 0 : ((currentPage - 1) * entriesPerPage) + 1} to{" "}
                    {Math.min(currentPage * entriesPerPage, filteredPayments.length)} of{" "}
                    {filteredPayments.length} entries
                  </div>
                  <div className="dt-footer-pagination">
                    <ul className="pagination pagination-sm">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                          <button className="page-link" onClick={() => setCurrentPage(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
        ) : (
          <span>No guardians!</span>
        )}
      </div>
    </div>
  );
};

export default PropertyPayments;
